// Inline citation marker parser. The backend LLM embeds `[[c0]]`, `[[c1]]`, …
// directly after facts in artifact `body` / `items[].text` so the operator can
// see *which* claim is supported by *which* quote (instead of a blob at the
// bottom of the artifact). The numeric token after `c` indexes into the
// artifact's `citations[]` array.
//
// Kept free of React imports so it can be unit-tested in isolation and so the
// renderer file (`InlineCitedText.tsx`) owns presentation concerns only.

export interface TextSegment {
  type: "text";
  content: string;
}

export interface MarkerSegment {
  type: "marker";
  /** Zero-based index into the citations array. */
  citationIndex: number;
}

export type CitationSegment = TextSegment | MarkerSegment;

// `[[cN]]` where N is one or more digits. The capturing group is reused to
// extract the index. We compile once at module scope; using a fresh RegExp per
// call would be wasteful and `matchAll` resets `lastIndex` for us.
const MARKER_RE = /\[\[c(\d+)\]\]/g;

/**
 * Split a text body into a sequence of text-and-marker segments. Adjacent text
 * is preserved verbatim (no whitespace collapsing — markdown / line breaks are
 * meaningful in `body`). Empty text segments at the head/tail of the input are
 * omitted so callers don't render empty spans.
 *
 * Examples
 * --------
 *   splitTextByMarkers("Hi [[c0]] there")
 *     → [{text "Hi "}, {marker 0}, {text " there"}]
 *
 *   splitTextByMarkers("plain text")
 *     → [{text "plain text"}]
 *
 *   splitTextByMarkers("")
 *     → []
 */
export function splitTextByMarkers(text: string): CitationSegment[] {
  if (typeof text !== "string" || text.length === 0) {
    return [];
  }

  const segments: CitationSegment[] = [];
  let cursor = 0;

  // Iterate via matchAll for a stable, single-pass parse. `match.index` is
  // guaranteed when the regex has the `g` flag.
  for (const match of text.matchAll(MARKER_RE)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ type: "text", content: text.slice(cursor, start) });
    }
    const idx = Number.parseInt(match[1] ?? "", 10);
    if (Number.isFinite(idx) && idx >= 0) {
      segments.push({ type: "marker", citationIndex: idx });
    }
    cursor = start + match[0].length;
  }

  if (cursor < text.length) {
    segments.push({ type: "text", content: text.slice(cursor) });
  }

  return segments;
}

/**
 * Quick check used by `ArtifactView` to decide between the inline renderer
 * (markers present) and the legacy bottom-block fallback (markers absent). We
 * test against the source pattern rather than re-running `splitTextByMarkers`
 * to avoid allocating segments just to throw them away.
 */
export function hasInlineMarkers(text: string): boolean {
  if (typeof text !== "string" || text.length === 0) return false;
  // RegExp.test mutates `lastIndex` on /g/-flagged regexes, so use a fresh
  // pattern here — keeps `splitTextByMarkers` deterministic across callers.
  return /\[\[c\d+\]\]/.test(text);
}
